import { effect, inject, Injectable, signal } from '@angular/core';
import { SegmentType, TimelineSegment, TimeSegment } from '../interfaces';
import { getDailyHours } from '../config/constants';
// Direct file import (not the services barrel) to avoid an import cycle:
// the barrel re-exports daily-route-list.ts, which imports this service.
import { EditRouteService } from '../components/daily-route-list/services/edit-route.service';

@Injectable({
  providedIn: 'root'
})
export class RouteTimelineService {
  // Service injections
  private _editRouteService = inject(EditRouteService);

  // State signals
  private _timelineSegments = signal<TimelineSegment[]>([]);
  public timelineSegments = this._timelineSegments.asReadonly();
  
  private _isDragging = signal(false);
  public isDragging = this._isDragging.asReadonly();
  
  // Setters
  public setTimelineSegments(timelineSegments: TimelineSegment[]): void {
    this._timelineSegments.set(timelineSegments);
  }
  
  public setIsDragging(isDragging: boolean): void {
    this._isDragging.set(isDragging);
  }
  
  // Effects
  /**
   * @description Generates the timeline segments for the selected date based on the provided points.
   */
  private _setTimelineSegments = effect(() => {
    const timelineSegments = this.generateTimeline();
    if (!timelineSegments) return;
    
    this._timelineSegments.set(timelineSegments);
  }, { allowSignalWrites: true });
  
  
  // Methods
   /**
   * @description Generates the timeline segments for the current time.
   */
   public generateTimeline(): TimelineSegment[] {

    return getDailyHours(this._editRouteService.routeStartHour()).map(hourStr => {
      const hour = parseInt(hourStr);

      const segments: TimeSegment[] = [
        this.buildSegment(hour, 0, 'big'),
        ...[15].map(min => this.buildSegment(hour, min, 'small')),
        this.buildSegment(hour, 30, 'medium'),
        ...[45].map(min => this.buildSegment(hour, min, 'small')),
      ];

      return { hour: hourStr, segments };
    });
  }

  /**
   * 
   * @returns a TimeSegment object representing the segment for the given hour and minute
   * @description Builds a TimeSegment object for the given hour and minute, 
   * determining its type.
   */
  private buildSegment(
    hour: number,
    minute: number,
    type: SegmentType,
  ): TimeSegment {
    const segmentDate = new Date();
    segmentDate.setHours(hour, minute, 0, 0);


    return {
      time: segmentDate.toTimeString().slice(0, 5),
      type,
    };
  }
}

